import { normalizeSourceTextUpload, SOURCE_TEXT_MAX_BYTES } from '@oncatch/types/source-text';
import type { FailedAttachmentDescriptor } from './api.ts';
import { prepareBackgroundImage } from './background-image.ts';
import { loadLocalImage } from './image-loader.ts';
// Widget attachment state machine — #161 (#5a).
//
// Manages per-slot lifecycle for screenshots (and later, recordings):
//   empty → reserving → uploading → uploaded
//                    ↘            ↘
//                     failed       failed (retry returns to reserving)
//
// Responsibilities:
//   - Cap slot count at maxSlots (default 5; widget total cap is 6 with the
//     video slot from #5c).
//   - Reserve a quota slot via api.reserveUpload, then PUT the blob to the
//     returned signed URL.
//   - Cache reservation IDs in slot state so submit retries reuse the same
//     IDs (no double-charge to workspace cap).
//   - Re-reserve + re-upload at submit time if a slot's TTL is within 30s of
//     expiring. Old blob is left for retention cron (~6h sweep).
//   - Release all pending reservations on widget close (best-effort; cron
//     handles the case where this never lands).
//   - Surface typed errors so the UI can render per-slot inline retry +
//     trigger the submit-blocker modal when any slot is in failed state.

import type {
  ApiClient,
  AttachmentContentType,
  ReserveErrorCode,
  ReserveUploadResponse,
  UploadViaProxyResponse,
} from './api.ts';
import {
  type CompressImageResult,
  compressImage,
  type ImageContentType,
} from './compression-client.ts';
import { markStage, measureStage } from './perf.ts';
import {
  type CompressVideoResult,
  compressVideo,
  type VideoContentType,
} from './video-compression-client.ts';

export type AttachmentSlotState =
  | 'local_ready'
  | 'compressing'
  | 'queued'
  | 'reserving'
  | 'uploading'
  | 'uploaded'
  | 'failed';

/** #135: pluggable image-compression entrypoint. Tests inject a stub. */
export type CompressImageFn = (input: {
  blob: Blob;
  contentType: ImageContentType;
  filename: string;
}) => Promise<CompressImageResult>;

/**
 * #135 PR B: pluggable video-compression entrypoint. Tests inject a stub.
 * #334 — accepts an optional `onProgress` callback so the manager can plumb
 * ffmpeg's progress events to the slot (and from there to the UI).
 * #334 (720p) — accepts an optional `maxHeight` so the recording path can
 * request a higher output resolution than the dragged-file default. Omitted
 * by `addFile(video)`, which keeps `compressVideo`'s 480p default.
 */
export type CompressVideoFn = (input: {
  blob: Blob;
  contentType: VideoContentType;
  filename: string;
  onProgress?: (fraction: number) => void;
  maxHeight?: number;
}) => Promise<CompressVideoResult>;

export interface AttachmentSlot {
  /** Local-only ID for UI keying. NOT the server reservation ID. */
  id: string;
  backgroundProcessing?: boolean;
  state: AttachmentSlotState;
  filename: string;
  contentType: AttachmentContentType;
  sizeBytes: number;
  /** Live reservation, set after reserveUpload succeeds. Cleared on re-reserve. */
  reservationId?: string;
  reservationCredentialGeneration?: number;
  reprepareRequired?: boolean;
  /** Reservation expiry (epoch ms). */
  expiresAt?: number;
  /** Source blob — kept in memory so we can re-upload on TTL expiry / retry. */
  blob?: Blob;
  /** Best-effort 96x96 image preview (data URL). Only for image/* slots. */
  thumbnailDataUrl?: string;
  /** Last error code (typed when known, otherwise opaque string). */
  errorCode?: string;
  /** Human-readable error for inline UI. */
  errorMessage?: string;
  /**
   * #334 — video compression progress fraction (0..1) while ffmpeg encodes.
   * Set only for video slots in the `compressing` state, and only when the
   * loaded ffmpeg core actually emits progress events. Stays `undefined` for
   * legacy cores so the UI can fall back to an indefinite spinner (AC3).
   */
  compressionProgress?: number;
  /**
   * #297 — non-default attachment_type hint. Set only for camera-recording
   * slots ('video_with_camera'); plain screenshots + screen recordings omit
   * it so the server falls back to its legacy contentType-derived enum.
   */
  attachmentType?: 'screenshot' | 'video' | 'video_with_camera' | 'audio';
}

export interface AttachmentManagerOptions {
  uploadPolicy?: () => unknown;
  backgroundAttachmentsEnabled?: () => boolean;
  sourceTextUploadsEnabled?: () => boolean;
  prepareImage?: (blob: Blob) => Promise<Blob>;
  workspaceId: string;
  /** Default 5. Total widget cap is 6 (this + 1 video slot from #5c). */
  maxSlots?: number;
  /** Pluggable for tests. Defaults to crypto.randomUUID(). */
  generateId?: () => string;
  /** Pluggable for tests. Defaults to Date.now. */
  now?: () => number;
  /**
   * #135: pluggable image-compression entrypoint. Default uses canvas → WebP
   * via `compressImage` from compression-client.ts. Tests inject a stub that
   * returns a passthrough result so they don't depend on the canvas API.
   */
  compress?: CompressImageFn;
  /**
   * #135 PR B: pluggable video-compression entrypoint. Default uses ffmpeg-
   * wasm via `compressVideo` from video-compression-client.ts. Tests inject
   * a stub that returns a passthrough so they don't depend on WebAssembly.
   */
  compressVideo?: CompressVideoFn;
}

const DEFAULT_MAX_SLOTS = 5;
const TTL_BUFFER_MS = 30_000;
/** Exported for #605's dropzone pre-check (don't annotate a doomed file). */
export const MAX_PRE_COMPRESSION_BYTES = 50 * 1024 * 1024;
/** Maximum source body accepted by the processor ingress, after client compression. */
export const MAX_UPLOAD_SOURCE_BYTES = 4 * 1024 * 1024;
/**
 * #334 (720p) — screen + camera recordings compress at 720p (not the 480p
 * dragged-file default) so screen-capture text/code stays legible for the QA
 * reviewer who watches these recordings as bug evidence. CRF is unchanged; the
 * larger-file tradeoff is accepted for now (revisit if upload/storage gets
 * slow). Scoped to addRecordingResult — the dormant addFile(video) path keeps
 * compressVideo's 480p default.
 */
const RECORDING_MAX_HEIGHT = 720;

export const ALLOWED_CONTENT_TYPES: ReadonlyArray<AttachmentContentType> = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'video/mp4',
  'video/webm',
  'audio/mp4',
  'audio/webm',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

/** Errors thrown by client-side validation BEFORE the server is called. */
export type LocalValidationError =
  | 'max_slots_reached'
  | 'source_text_too_large'
  | 'unsupported_type'
  | 'file_too_large'
  | 'has_failed_slots'
  | 'ttl_reserve_failed'
  | 'attachment_credentials_changed';

export class AttachmentValidationError extends Error {
  constructor(public readonly reason: LocalValidationError) {
    super(reason);
    this.name = 'AttachmentValidationError';
  }
}

export class AttachmentManager {
  private slots: AttachmentSlot[] = [];
  private readonly sourceTextEnabled: () => boolean;
  private readonly backgroundEnabled: () => boolean;
  private readonly prepareImage: (blob: Blob) => Promise<Blob>;
  private uploadTail: Promise<void> = Promise.resolve();
  private uploadJobs = new Map<AttachmentSlot, Promise<void>>();
  private listeners = new Set<() => void>();
  private readonly legacyMaxSlots: number;
  private readonly policyProvider: () => unknown;
  private draftPolicy: { maxFiles: number; maxTotalBytes: number } | undefined;
  private currentPolicy(): { maxFiles: number; maxTotalBytes: number } {
    if (this.draftPolicy) return this.draftPolicy;
    const value = this.policyProvider() as {
      version?: unknown;
      maxFiles?: unknown;
      maxTotalBytes?: unknown;
    } | null;
    if (
      value?.version === 1 &&
      ((value.maxFiles === 5 && value.maxTotalBytes === 10485760) ||
        (value.maxFiles === 10 && value.maxTotalBytes === 52428800))
    )
      return { maxFiles: value.maxFiles, maxTotalBytes: value.maxTotalBytes as number };
    return value == null
      ? { maxFiles: this.legacyMaxSlots, maxTotalBytes: Infinity }
      : { maxFiles: 5, maxTotalBytes: 10485760 };
  }
  private get maxSlots(): number {
    return this.currentPolicy().maxFiles;
  }
  private preparedSourceBytes(): number {
    return this.slots.reduce(
      (total, slot) =>
        total +
        (slot.state !== 'compressing' && slot.state !== 'failed' ? (slot.blob?.size ?? 0) : 0),
      0,
    );
  }

  private readonly generateId: () => string;
  private readonly now: () => number;
  private readonly compress: CompressImageFn;
  private readonly compressVid: CompressVideoFn;
  /** #256 — needed for the upload_pipeline_failure relay to /widget-error. */
  private readonly workspaceId: string;

  constructor(
    private readonly api: ApiClient,
    options: AttachmentManagerOptions,
  ) {
    this.sourceTextEnabled = options.sourceTextUploadsEnabled ?? (() => false);
    this.backgroundEnabled = options.backgroundAttachmentsEnabled ?? (() => false);
    this.prepareImage = options.prepareImage ?? prepareBackgroundImage;
    this.workspaceId = options.workspaceId;
    this.legacyMaxSlots = options.maxSlots ?? DEFAULT_MAX_SLOTS;
    this.policyProvider = options.uploadPolicy ?? (() => undefined);
    this.generateId = options.generateId ?? (() => generateUuid());
    this.now = options.now ?? (() => Date.now());
    this.compress = options.compress ?? ((input) => compressImage(input));
    this.compressVid =
      options.compressVideo ??
      ((input) =>
        compressVideo(
          { blob: input.blob, contentType: input.contentType, filename: input.filename },
          {
            // #334 — forward ffmpeg's 0..1 progress fraction up to the manager.
            ...(input.onProgress
              ? { onProgress: ({ fraction }: { fraction: number }) => input.onProgress?.(fraction) }
              : {}),
            // #334 (720p) — forward an explicit output height when the caller
            // requests one (recordings pass 720). When omitted (dragged-file
            // addFile path), compressVideo falls back to its 480p default.
            ...(input.maxHeight !== undefined ? { maxHeight: input.maxHeight } : {}),
          },
        ));
  }

  // ============================================================================
  // READ API — for UI rendering
  // ============================================================================

  usesBackgroundProcessing(): boolean {
    // A config refresh cannot mix processing contracts inside one report draft.
    return this.slots.length
      ? this.slots[0]?.backgroundProcessing === true
      : this.backgroundEnabled() === true;
  }

  acceptsSourceTextUploads(): boolean {
    return this.usesBackgroundProcessing() && this.sourceTextEnabled() === true;
  }

  preparedCredentialGeneration(): number | undefined {
    const slots = this.slots.filter(
      (slot) => slot.backgroundProcessing && slot.state === 'uploaded',
    );
    const generation = slots[0]?.reservationCredentialGeneration;
    return slots.every((slot) => slot.reservationCredentialGeneration === generation)
      ? generation
      : undefined;
  }

  failedAttachmentDescriptors(): FailedAttachmentDescriptor[] {
    return this.slots
      .filter((slot) => slot.backgroundProcessing && slot.state === 'failed')
      .map((slot) => ({
        clientId: slot.id,
        filename: slot.filename,
        contentType: slot.contentType.startsWith('image/')
          ? ('image/png' as const)
          : slot.contentType,
        ...(slot.attachmentType ? { attachmentType: slot.attachmentType } : {}),
        error:
          slot.errorCode === 'LOCAL_PROCESSING_FAILED' || slot.errorCode === 'FILE_TOO_LARGE'
            ? 'local_processing_failed'
            : 'upload_failed',
      }));
  }

  private async finishSelection(slot: AttachmentSlot): Promise<void> {
    if (!this.slots.includes(slot) || slot.state === 'failed') return;
    await this.reserveAndUpload(slot);
  }

  getSlots(): ReadonlyArray<AttachmentSlot> {
    return this.slots;
  }

  count(): number {
    return this.slots.length;
  }

  remaining(): number {
    return Math.max(0, this.maxSlots - this.slots.length);
  }

  hasFailedSlots(): boolean {
    return this.slots.some((s) => s.state === 'failed');
  }

  /**
   * #533 — true while ANY slot still has work in flight.
   *
   * `compressing` counts. It didn't before, and that omission was half of the
   * silent-drop bug: a screenshot still being re-encoded at submit time was
   * neither reported as pending nor collected by prepareForSubmit, so it
   * vanished from the submission with no signal to the reporter or to admin.
   * This method had zero callers before #533; the submit path now gates on it.
   */
  hasPendingUploads(): boolean {
    return this.slots.some(
      (s) =>
        s.state === 'compressing' ||
        s.state === 'queued' ||
        s.state === 'reserving' ||
        s.state === 'uploading',
    );
  }

  /**
   * #533 — resolve once nothing is in flight.
   *
   * Resolves immediately when there's no pending work; otherwise subscribes and
   * resolves on the first notify that finds the pipeline clear. Every slot
   * reaches a terminal state (`uploaded` or `failed`), so the caller always
   * continues into proceed-or-blocker.
   *
   * The wait itself is deliberately UNTIMED. It's bounded from below by two
   * things: the proxy PUT carries its own abort ceiling (api.ts), so a hung
   * upload becomes a `failed` slot rather than an infinite pend; and `remove()`
   * settles a stuck slot instantly, which is the reporter's escape hatch.
   */
  async waitForUploadsToSettle(): Promise<void> {
    if (!this.hasPendingUploads()) return;
    await new Promise<void>((resolve) => {
      const unsubscribe = this.subscribe(() => {
        if (this.hasPendingUploads()) return;
        unsubscribe();
        resolve();
      });
    });
  }

  /** Number of slots holding a live (uncommitted) reservation. */
  pendingReservationCount(): number {
    return this.slots.filter((s) => s.reservationId && s.state !== 'uploaded').length;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  // ============================================================================
  // MUTATING API
  // ============================================================================

  /**
   * Add a file (from input, drag-drop, paste, or getDisplayMedia frame).
   * Throws AttachmentValidationError on local validation failure (caller
   * should surface to UI without going to network). Otherwise resolves once
   * reserve + upload have completed (success OR failure — caller checks
   * slot.state to know which).
   *
   * #333 — `options.skipImageCompression` lets a caller bypass the canvas
   * decode-paint-re-encode round-trip for images it already produced in a
   * suitable format. The widget's own capture paths (whole-page / region /
   * pin) emit PNGs straight from `html-to-image`; re-encoding those to WebP
   * burns CPU for a file we just made. User-uploaded images (a hypothetical
   * future file-picker path) omit the flag and still get WebP compression.
   * The flag is image-only — videos always run the ffmpeg pipeline.
   */
  async addFile(
    file: File | Blob,
    filename: string,
    options: { skipImageCompression?: boolean } = {},
  ): Promise<string> {
    if (this.slots.length >= this.maxSlots) {
      throw new AttachmentValidationError('max_slots_reached');
    }

    const sourceText = normalizeSourceTextUpload(filename, file.type);
    if (sourceText && !this.acceptsSourceTextUploads())
      throw new AttachmentValidationError('unsupported_type');
    const contentType = sourceText?.contentType ?? normalizeContentType(file.type);
    if (sourceText && file.size > SOURCE_TEXT_MAX_BYTES)
      throw new AttachmentValidationError('source_text_too_large');
    if (!contentType) {
      throw new AttachmentValidationError('unsupported_type');
    }

    if (file.size > MAX_PRE_COMPRESSION_BYTES) {
      throw new AttachmentValidationError('file_too_large');
    }

    if (contentType.startsWith('application/') && file.size > MAX_UPLOAD_SOURCE_BYTES) {
      throw new AttachmentValidationError('file_too_large');
    }
    const isVideo = contentType.startsWith('video/');
    // #333 — widget-produced PNGs (whole-page / region / pin captures) pass
    // skipImageCompression so we don't re-encode a file we just generated.
    const background = this.usesBackgroundProcessing();
    const willCompressImage =
      contentType.startsWith('image/') && (background || !options.skipImageCompression);
    const slot: AttachmentSlot = {
      id: this.generateId(),
      backgroundProcessing: this.usesBackgroundProcessing(),
      // #135: images traverse `compressing → reserving → ...`.
      // #135 PR B: videos do the same via the ffmpeg-wasm pipeline.
      // #333: compression-skipped images skip `compressing` and reserve
      // directly — there's no decode/paint/re-encode work to show.
      state: willCompressImage || isVideo ? 'compressing' : 'reserving',
      filename: sourceText?.filename ?? sanitizeFilename(filename),
      contentType,
      sizeBytes: file.size,
      blob: file,
    };
    this.draftPolicy ??= this.currentPolicy();
    this.slots.push(slot);
    this.notify();
    if (background && contentType.startsWith('image/')) {
      void this.makeThumbnail(file).then((url) => {
        if (url && this.slots.includes(slot)) {
          slot.thumbnailDataUrl = url;
          this.notify();
        }
      });
    }

    if (willCompressImage) {
      if (background) {
        try {
          const prepared = await this.prepareImage(file);
          slot.blob = prepared;
          slot.contentType = 'image/png';
          slot.sizeBytes = prepared.size;
          slot.filename = `${slot.filename.replace(/\.[^.]+$/, '')}.png`;
        } catch {
          slot.state = 'failed';
          slot.errorCode = 'LOCAL_PROCESSING_FAILED';
          slot.errorMessage = 'Image could not be prepared. Your report can still be sent.';
          this.notify();
        }
      } else await this.compressIfImage(slot);
    } else if (isVideo) {
      await this.compressIfVideo(slot);
    }

    if (slot.contentType.startsWith('image/')) {
      // Best-effort thumbnail off the (possibly-compressed) blob; don't block
      // upload on it.
      void this.makeThumbnail(slot.blob ?? file).then((url) => {
        if (url && this.slots.includes(slot)) {
          slot.thumbnailDataUrl = url;
          this.notify();
        }
      });
    }

    await this.finishSelection(slot);
    return slot.id;
  }

  /** User clicks retry on a failed slot. Releases any stale reservation, then re-runs the pipeline. */
  async retry(slotId: string): Promise<void> {
    const slot = this.slots.find((s) => s.id === slotId);
    if (!slot || slot.state !== 'failed' || !slot.blob) return;

    if (slot.reservationId) {
      // Best-effort release of a stale reservation. Failure is fine — TTL +
      // cron clean it up. Don't await sequentially to keep retry snappy.
      void this.api.releaseUpload({ reservationId: slot.reservationId }).catch(() => undefined);
      slot.reservationId = undefined;
      slot.expiresAt = undefined;
    }

    if (slot.backgroundProcessing) {
      if (slot.errorCode === 'LOCAL_PROCESSING_FAILED' && slot.contentType.startsWith('image/')) {
        slot.state = 'compressing';
        this.notify();
        try {
          const prepared = await this.prepareImage(slot.blob);
          if (!this.slots.includes(slot)) return;
          slot.blob = prepared;
          slot.contentType = 'image/png';
          slot.sizeBytes = prepared.size;
          slot.filename = `${slot.filename.replace(/\.[^.]+$/, '')}.png`;
        } catch {
          slot.state = 'failed';
          this.notify();
          return;
        }
      }
      slot.state = 'local_ready';
      slot.errorCode = undefined;
      slot.errorMessage = undefined;
      this.notify();
      await this.reserveAndUpload(slot);
      return;
    }
    await this.reserveAndUpload(slot);
  }

  /** Remove a slot. Releases the reservation if it's still live (not committed). */
  async remove(slotId: string): Promise<void> {
    const idx = this.slots.findIndex((s) => s.id === slotId);
    if (idx === -1) return;
    const slot = this.slots[idx];
    this.slots.splice(idx, 1);
    this.notify();

    if (slot?.reservationId && (slot.backgroundProcessing || slot.state !== 'uploaded')) {
      await this.api.releaseUpload({ reservationId: slot.reservationId }).catch(() => undefined);
    }
  }

  /**
   * Called right before submission. Re-reserves + re-uploads any slot whose
   * reservation TTL is within TTL_BUFFER_MS of expiring. Returns the array of
   * {reservationId} to forward to /api/v1/submissions.
   *
   * Throws AttachmentValidationError('has_failed_slots') if any slot is in
   * failed state — the widget UI should have shown the submit-blocker modal
   * first and either resolved the failure or removed the slot.
   *
   * Throws AttachmentValidationError('ttl_reserve_failed') if a TTL re-reserve
   * fails. Caller should surface a generic error and let the user retry.
   */
  async prepareForSubmit(): Promise<
    Array<{
      reservationId: string;
      attachmentType?: 'screenshot' | 'video' | 'video_with_camera' | 'audio';
    }>
  > {
    // #533 — the settle gate, and the reason it lives HERE and not only in the
    // UI. The collection loop below includes only `uploaded` slots, so a slot
    // still compressing / reserving / uploading was silently omitted from the
    // submission: invisible to the reporter (they saw a green success state)
    // and invisible to admin (the #256 partial-failure notice only reports
    // reservations that were actually sent). Waiting here makes "an in-flight
    // slot is never dropped" a manager-level invariant that survives a future
    // caller forgetting the UI gate.
    await this.waitForUploadsToSettle();

    if (this.usesBackgroundProcessing() && !(await this.api.getFreshToken()))
      throw new AttachmentValidationError('attachment_credentials_changed');
    if (this.slots.some((slot) => slot.errorCode === 'CREDENTIAL_CHANGED'))
      throw new AttachmentValidationError('attachment_credentials_changed');
    for (const slot of [...this.slots]) {
      const repairingCredential =
        slot.backgroundProcessing === true &&
        slot.reservationCredentialGeneration !== undefined &&
        slot.reservationCredentialGeneration !== this.api.getCredentialGeneration();
      if ((repairingCredential || slot.reprepareRequired) && slot.state === 'failed')
        throw new AttachmentValidationError('attachment_credentials_changed');
      if (
        slot.backgroundProcessing &&
        slot.state === 'uploaded' &&
        slot.reservationCredentialGeneration !== this.api.getCredentialGeneration()
      ) {
        slot.reprepareRequired = true;
        slot.reservationId = undefined;
        slot.expiresAt = undefined;
        slot.state = 'local_ready';
      }
      if (slot.backgroundProcessing && slot.state === 'local_ready')
        await this.reserveAndUpload(slot);
      if ((repairingCredential || slot.reprepareRequired) && slot.state !== 'uploaded')
        throw new AttachmentValidationError('attachment_credentials_changed');
    }
    if (
      this.slots.some(
        (slot) =>
          slot.backgroundProcessing &&
          (slot.errorCode === 'CREDENTIAL_CHANGED' ||
            (slot.state === 'uploaded' &&
              slot.reservationCredentialGeneration !== this.api.getCredentialGeneration())),
      )
    )
      throw new AttachmentValidationError('attachment_credentials_changed');
    if (this.slots.some((slot) => !slot.backgroundProcessing && slot.state === 'failed')) {
      throw new AttachmentValidationError('has_failed_slots');
    }

    const cutoff = this.now() + TTL_BUFFER_MS;
    for (const slot of this.slots) {
      if (slot.state === 'uploaded' && slot.expiresAt !== undefined && slot.expiresAt < cutoff) {
        // Stale reservation — re-reserve + re-upload. Old blob orphans for
        // the retention cron (#139 H1) to sweep within ~6h.
        if (slot.backgroundProcessing) slot.reprepareRequired = true;
        slot.reservationId = undefined;
        slot.expiresAt = undefined;
        await this.reserveAndUpload(slot);
        if (slot.state !== 'uploaded' && !slot.backgroundProcessing) {
          throw new AttachmentValidationError('ttl_reserve_failed');
        }
      }
    }

    if (
      this.slots.some(
        (slot) =>
          slot.backgroundProcessing &&
          (slot.errorCode === 'CREDENTIAL_CHANGED' ||
            slot.reprepareRequired ||
            (slot.state === 'uploaded' &&
              slot.reservationCredentialGeneration !== this.api.getCredentialGeneration())),
      )
    )
      throw new AttachmentValidationError('attachment_credentials_changed');

    const out: Array<{
      reservationId: string;
      attachmentType?: 'screenshot' | 'video' | 'video_with_camera' | 'audio';
    }> = [];
    for (const slot of this.slots) {
      if (slot.state === 'uploaded' && slot.reservationId) {
        const entry: {
          reservationId: string;
          attachmentType?: 'screenshot' | 'video' | 'video_with_camera' | 'audio';
        } = { reservationId: slot.reservationId };
        if (slot.attachmentType) entry.attachmentType = slot.attachmentType;
        out.push(entry);
      }
    }
    return out;
  }

  /**
   * Release all pending (non-committed) reservations. Best-effort, fire-and-
   * forget semantics. Used on widget close so workspace cap doesn't sit on
   * abandoned reservations for the full 5-min TTL.
   */
  async releaseAllPending(): Promise<void> {
    const releases: Array<Promise<unknown>> = [];
    for (const slot of this.slots) {
      if (slot.reservationId && slot.state !== 'uploaded') {
        releases.push(
          this.api.releaseUpload({ reservationId: slot.reservationId }).catch(() => undefined),
        );
      }
    }
    if (releases.length > 0) {
      await Promise.all(releases);
    }
  }

  /** Drop all slots. Called after a successful submission. */
  reset(): void {
    if (this.slots.length === 0) return;
    this.slots = [];
    this.draftPolicy = undefined;
    this.notify();
  }

  /**
   * #163 PR B: add a screen recording's final blob as a slot.
   *
   * #334 — drives the SAME compression-with-progress path as addFile(video).
   * The original assumption (that recording-flow.ts already ran the blob
   * through compressVideo) was false: runRecordingFlow returns the raw
   * MediaRecorder output untouched, so a screen recording was both uploaded
   * uncompressed AND showed a frozen/indefinite spinner during the encode
   * #508 was filed to make determinate. Routing recordings through
   * compressIfVideo fixes both: ffmpeg's 0..1 progress events now land on
   * slot.compressionProgress (notifying the UI for the determinate bar in
   * slotMarkup), and the blob is re-encoded to H.264 before the reservation
   * so the workspace cap math sees the post-compression size. Failure stays
   * passthrough (compressIfVideo never throws), so an unencodable recording
   * still uploads its raw bytes — same safety contract as addFile().
   *
   * Throws AttachmentValidationError('max_slots_reached') when the slot cap
   * is reached. Caller is expected to gate the Record button on remaining()
   * upstream, but the throw is the second line of defense.
   */
  async addRecordingResult(result: {
    blob: Blob;
    filename: string;
    contentType: 'video/mp4' | 'video/webm';
    /**
     * #297 — when the recording came from the camera-overlay flow, callers
     * pass 'video_with_camera' so the server-side attachment_type ends up
     * distinct from a plain screen recording. Omit (or pass 'video') for
     * legacy screen-only recordings.
     */
    attachmentType?: 'video' | 'video_with_camera';
  }): Promise<string> {
    if (this.slots.length >= this.maxSlots) {
      throw new AttachmentValidationError('max_slots_reached');
    }
    if (result.blob.size > MAX_PRE_COMPRESSION_BYTES) {
      throw new AttachmentValidationError('file_too_large');
    }

    const slot: AttachmentSlot = {
      id: this.generateId(),
      backgroundProcessing: this.usesBackgroundProcessing(),
      // #334 — start in `compressing` (matches addFile(video)) so the slot
      // renders the progress overlay from the first frame instead of jumping
      // straight to the reserving spinner.
      state: 'compressing',
      filename: sanitizeFilename(result.filename),
      contentType: result.contentType,
      sizeBytes: result.blob.size,
      blob: result.blob,
      attachmentType: result.attachmentType,
    };
    this.draftPolicy ??= this.currentPolicy();
    this.slots.push(slot);
    this.notify();

    // #334 — same compression-with-progress path as addFile(video). Sets
    // slot.compressionProgress from ffmpeg's events (driving the determinate
    // bar), re-encodes the blob in place, and clears progress before reserve.
    // Never throws — passthrough on any failure keeps the upload pipeline alive.
    // #334 (720p) — recordings encode at 720p (vs the dragged-file 480p
    // default) for legible screen-capture text. Scoped to this path only.
    await this.compressIfVideo(slot, RECORDING_MAX_HEIGHT);

    await this.finishSelection(slot);
    return slot.id;
  }

  /** Add a microphone-only recording without sending it through video compression. */
  async addAudioRecordingResult(result: {
    blob: Blob;
    filename: string;
    contentType: 'audio/mp4' | 'audio/webm';
  }): Promise<string> {
    if (this.slots.length >= this.maxSlots) {
      throw new AttachmentValidationError('max_slots_reached');
    }
    if (result.blob.size > MAX_PRE_COMPRESSION_BYTES) {
      throw new AttachmentValidationError('file_too_large');
    }
    const slot: AttachmentSlot = {
      id: this.generateId(),
      backgroundProcessing: this.usesBackgroundProcessing(),
      state: 'reserving',
      filename: sanitizeFilename(result.filename),
      contentType: result.contentType,
      sizeBytes: result.blob.size,
      blob: result.blob,
      attachmentType: 'audio',
    };
    this.draftPolicy ??= this.currentPolicy();
    this.slots.push(slot);
    this.notify();
    await this.finishSelection(slot);
    return slot.id;
  }

  /** Number of slots holding a video (any state). */
  countVideos(): number {
    return this.slots.filter((s) => s.contentType.startsWith('video/')).length;
  }

  /** One submission may contain one recording, regardless of its capture mode. */
  countRecordings(): number {
    return this.slots.filter(
      (s) => s.contentType.startsWith('video/') || s.contentType.startsWith('audio/'),
    ).length;
  }

  // ============================================================================
  // INTERNALS
  // ============================================================================

  /**
   * #135: compress the slot's blob in place. On success, replaces blob,
   * contentType, filename, and sizeBytes with the compressed values. On any
   * failure (canvas unavailable, decode error, output larger than input),
   * leaves the slot unchanged — the upload pipeline still proceeds with raw
   * bytes. Compression failure NEVER fails a slot; the layered cap defense
   * already protects the workspace from oversized uploads.
   */
  private async compressIfImage(slot: AttachmentSlot): Promise<void> {
    if (!slot.blob) return;
    if (!slot.contentType.startsWith('image/')) return;

    // #579 — marked in a try/finally so a compression that throws still records
    // how long it burned before failing.
    markStage('compress', slot.id, 'start');
    try {
      const result = await this.compress({
        blob: slot.blob,
        contentType: slot.contentType as ImageContentType,
        filename: slot.filename,
      });
      if (result.outcome === 'compressed') {
        slot.blob = result.blob;
        slot.contentType = result.contentType;
        slot.filename = result.filename;
        slot.sizeBytes = result.blob.size;
      }
      // On 'passthrough', leave slot fields untouched — original blob is fine.
    } catch {
      // Compression itself crashed (shouldn't happen with the passthrough
      // model in compression-client, but defensive). Treat as passthrough.
    } finally {
      markStage('compress', slot.id, 'end');
      measureStage('compress', slot.id);
    }
  }

  /**
   * #135 PR B: same shape as compressIfImage but for videos. Uses the lazy-
   * loaded ffmpeg-wasm pipeline from video-compression-client.ts. Any
   * failure (CSP block, WASM unavailable, exec error, output ≥ input)
   * leaves the slot's blob untouched — upload proceeds with raw bytes.
   * Compression failure NEVER fails a slot; the layered cap defense
   * already protects the workspace.
   */
  private async compressIfVideo(slot: AttachmentSlot, maxHeight?: number): Promise<void> {
    if (!slot.blob) return;
    if (!slot.contentType.startsWith('video/')) return;

    markStage('compress', slot.id, 'start');
    try {
      const result = await this.compressVid({
        blob: slot.blob,
        contentType: slot.contentType as VideoContentType,
        filename: slot.filename,
        // #334 (720p) — recordings pass an explicit height; dragged-file
        // videos pass undefined and keep compressVideo's 480p default.
        ...(maxHeight !== undefined ? { maxHeight } : {}),
        // #334 — record ffmpeg's 0..1 progress on the slot + notify so the
        // UI can render a bar. Monotonic guard: ffmpeg can emit a final
        // event with a stale lower fraction; never let the bar go backwards.
        // No event ever fires for legacy cores, so compressionProgress stays
        // undefined and the UI falls back to the indefinite spinner (AC3).
        onProgress: (fraction) => {
          const next = clampFraction(fraction);
          if (slot.compressionProgress === undefined || next > slot.compressionProgress) {
            slot.compressionProgress = next;
            this.notify();
          }
        },
      });
      if (result.outcome === 'compressed') {
        slot.blob = result.blob;
        slot.contentType = result.contentType;
        slot.filename = result.filename;
        slot.sizeBytes = result.blob.size;
      }
    } catch {
      // Defensive — compressVideo's own contract is passthrough-on-error.
    } finally {
      // Clear progress before reserve/upload so a stale fraction doesn't
      // linger on the slot once encoding is done.
      slot.compressionProgress = undefined;
      markStage('compress', slot.id, 'end');
      measureStage('compress', slot.id);
    }
  }

  private reserveAndUpload(slot: AttachmentSlot): Promise<void> {
    const existing = this.uploadJobs.get(slot);
    if (existing) return existing;
    if (!slot.blob || !this.slots.includes(slot)) return Promise.resolve();
    // Compression stays parallel. Reserve only when this slot reaches the head,
    // so a waiting file cannot consume its reservation TTL or scanner capacity.
    const queued = this.uploadTail
      .then(async () => {
        if (!this.slots.includes(slot)) return;
        try {
          await this.reserveAndUploadNow(slot);
        } catch {
          if (!this.slots.includes(slot)) return;
          slot.state = 'failed';
          slot.errorCode = 'upload_network_error';
          slot.errorMessage = mapUploadError({ status: 0, error: 'network_error' });
          this.notify();
        }
      })
      .finally(() => {
        this.uploadJobs.delete(slot);
      });
    this.uploadJobs.set(slot, queued);
    this.uploadTail = queued.catch(() => undefined);
    slot.state = 'queued';
    slot.errorCode = undefined;
    slot.errorMessage = undefined;
    this.notify();
    return queued;
  }

  private async reserveAndUploadNow(slot: AttachmentSlot): Promise<void> {
    if (!slot.blob) return;
    if (this.preparedSourceBytes() > this.currentPolicy().maxTotalBytes) {
      slot.state = 'failed';
      slot.errorCode = 'REPORT_STORAGE_LIMIT';
      slot.errorMessage =
        'These files exceed the report upload limit. Remove a file and try again.';
      this.notify();
      return;
    }

    if (slot.contentType === 'text/plain' && slot.blob.size > SOURCE_TEXT_MAX_BYTES) {
      slot.state = 'failed';
      slot.errorCode = 'FILE_TOO_LARGE';
      slot.errorMessage = 'Text files must be 1 MB or smaller.';
      this.notify();
      return;
    }
    if (slot.blob.size > MAX_UPLOAD_SOURCE_BYTES) {
      slot.state = 'failed';
      slot.errorCode = 'FILE_TOO_LARGE';
      slot.errorMessage =
        'File exceeds the 4 MiB upload limit after compression. Try a smaller file or shorter recording.';
      this.notify();
      return;
    }

    slot.state = 'reserving';
    slot.errorCode = undefined;
    slot.errorMessage = undefined;
    this.notify();

    // #579 — the reserve round trip. Measured separately from `upload` because
    // they answer different questions: reserve is a small JSON call whose cost
    // is latency, upload is bytes on the wire.
    markStage('reserve', slot.id, 'start');
    let reserve: ReserveUploadResponse;
    try {
      reserve = await this.api.reserveUpload({
        filename: slot.filename,
        sizeBytes: slot.sizeBytes,
        contentType: slot.contentType,
        ...(slot.backgroundProcessing ? { processingMode: 'background' as const } : {}),
      });
    } finally {
      markStage('reserve', slot.id, 'end');
      measureStage('reserve', slot.id);
    }

    if (!this.slots.includes(slot)) {
      if (reserve.ok)
        void this.api
          .releaseUpload({ reservationId: reserve.reservationId })
          .catch(() => undefined);
      return;
    }

    if (!reserve.ok) {
      slot.state = 'failed';
      slot.errorCode = reserve.error;
      slot.errorMessage = mapReserveError(reserve);
      this.notify();
      return;
    }

    if (
      slot.backgroundProcessing &&
      reserve.credentialGeneration !== this.api.getCredentialGeneration()
    ) {
      slot.state = 'local_ready';
      slot.errorCode = 'CREDENTIAL_CHANGED';
      this.notify();
      return;
    }
    slot.reservationCredentialGeneration = reserve.credentialGeneration;
    slot.reservationId = reserve.reservationId;
    slot.expiresAt = Date.parse(reserve.expiresAt);
    slot.state = 'uploading';
    this.notify();

    // #480 — upload THROUGH admin.oncatch.app (already CSP-allowlisted) instead
    // of directly to the `*.supabase.co` signed URL. The server derives the
    // destination path from the reservationId, so a strict host `connect-src`
    // that only allows admin.oncatch.app no longer blocks screenshots/videos.
    markStage('upload', slot.id, 'start');
    let put: UploadViaProxyResponse;
    try {
      put = await this.api.uploadViaProxy({
        reservationId: reserve.reservationId,
        blob: slot.blob,
        contentType: slot.contentType,
        ...(slot.backgroundProcessing
          ? {
              processingMode: 'background' as const,
              credentialGeneration: slot.reservationCredentialGeneration,
            }
          : {}),
      });
    } finally {
      markStage('upload', slot.id, 'end');
      measureStage('upload', slot.id);
    }

    if (!this.slots.includes(slot)) return;

    if (!put.ok && slot.backgroundProcessing && put.error === 'credential_changed') {
      slot.reservationId = undefined;
      slot.expiresAt = undefined;
      slot.state = 'local_ready';
      slot.errorCode = 'CREDENTIAL_CHANGED';
      this.notify();
      return;
    }
    if (!put.ok) {
      slot.state = 'failed';
      slot.errorCode = `upload_${put.error}`;
      slot.errorMessage = mapUploadError(put);
      this.notify();
      // #256 — admin-relay observability for silent upload failures. The slot
      // already shows the user a per-slot error UI and the submit-blocker modal
      // gates submission, but this gives us prod visibility into the upload
      // failure rate. Fire-and-forget — submitWidgetError can never throw back
      // at us. Includes content type + post-compression bytes + reservation ID
      // in context so we can root-cause patterns across many failures.
      void this.api.submitWidgetError({
        workspaceId: this.workspaceId,
        errorKind: 'upload_pipeline_failure',
        message: `widget upload via proxy failed: ${put.error} (status ${put.status})`,
        context: {
          stage: 'upload_via_proxy',
          content_type: slot.contentType,
          size_bytes: slot.sizeBytes,
          reservation_id: slot.reservationId ?? 'unknown',
          http_status: put.status,
          error_code: put.error,
        },
      });
      return;
    }

    slot.reprepareRequired = false;
    slot.state = 'uploaded';
    this.notify();
  }

  private notify(): void {
    for (const l of this.listeners) {
      try {
        l();
      } catch {
        // Listener errors must not break state transitions.
      }
    }
  }

  private async makeThumbnail(blob: Blob): Promise<string | null> {
    if (typeof document === 'undefined' || typeof Image === 'undefined') return null;
    try {
      const img = await loadLocalImage(blob);
      const canvas = document.createElement('canvas');
      canvas.width = 96;
      canvas.height = 96;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      const side = Math.min(img.naturalWidth, img.naturalHeight);
      const sx = (img.naturalWidth - side) / 2;
      const sy = (img.naturalHeight - side) / 2;
      ctx.drawImage(img, sx, sy, side, side, 0, 0, 96, 96);
      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  }
}

// ============================================================================
// HELPERS
// ============================================================================

function normalizeContentType(raw: string): AttachmentContentType | null {
  const lower = raw.toLowerCase();
  if (lower === 'image/jpg') return 'image/jpeg';
  if ((ALLOWED_CONTENT_TYPES as ReadonlyArray<string>).includes(lower)) {
    return lower as AttachmentContentType;
  }
  return null;
}

function sanitizeFilename(raw: string): string {
  const cleaned = raw.replace(/[\\/]/g, '_').replace(/\.\./g, '_').trim();
  const truncated = cleaned.slice(0, 200);
  return truncated.length > 0 ? truncated : 'attachment';
}

function mapReserveError(reserve: {
  error: ReserveErrorCode;
  maxFileBytes?: number;
  retryAfterSeconds?: number;
}): string {
  switch (reserve.error) {
    case 'REPORT_STORAGE_LIMIT':
      return 'These files exceed the report upload limit. Remove a file and try again.';
    case 'STORAGE_FULL':
      return 'Workspace storage limit reached.';
    case 'FILE_TOO_LARGE':
      return reserve.maxFileBytes
        ? `File too large (max ${Math.floor(reserve.maxFileBytes / 1024 / 1024)} MiB).`
        : 'File too large.';
    case 'RATE_LIMIT_HIT':
    case 'IP_RATE_LIMIT':
      return reserve.retryAfterSeconds
        ? `Try again in ${Math.max(1, Math.ceil(reserve.retryAfterSeconds / 60))} min.`
        : 'Try again in a moment.';
    case 'DAILY_QUOTA_HIT':
      return 'Daily upload quota reached.';
    case 'DOCUMENT_REJECTED':
      return 'File could not be safely processed. Try a different file.';
    case 'CONTENT_TYPE_REJECTED':
      return 'File type not supported.';
    case 'INVALID_SIZE':
      return 'Invalid file size.';
    case 'INVALID_TOKEN':
    case 'WIDGET_MISCONFIGURED':
      return 'Connection issue. Refresh and try again.';
    default:
      return 'Upload failed. Please try again.';
  }
}

function mapUploadError(put: { error: string; status: number }): string {
  if (put.error === 'network_error') return 'Network error. Try again.';
  if (put.status === 403) return 'Upload link expired. Try again.';
  return 'Upload failed. Try again.';
}

/** #334 — clamp ffmpeg's progress fraction to [0, 1]; coerce NaN/Inf to 0. */
function clampFraction(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for non-secure contexts (HTTP customer origins).
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}
